import { createHmac, timingSafeEqual } from 'node:crypto'

export interface RefsPayload {
  refs: Record<string, Record<string, unknown>[]>
  testRunId: string
  environment: string
  /**
   * Captured at `up` time so `down` can topo-sort teardown without
   * re-parsing the create payload. Optional — older tokens omitted it
   * and `down` falls back to refs-key insertion order.
   */
  aliasDependencies?: Record<string, string[]>
  aliasOwnerModel?: Record<string, string>
}

/**
 * Sign refs into a JWT-like token (header.payload.signature).
 * Uses HMAC-SHA256 — not a full JWT library to avoid dependencies.
 */
export function signRefs(payload: RefsPayload, secret: string): string {
  const header = base64url({ alg: 'HS256', typ: 'REFS' })
  const body = base64url(payload)
  const signature = hmac(`${header}.${body}`, secret)
  return `${header}.${body}.${signature}`
}

/**
 * Verify and decode a refs token. Returns the payload or throws.
 */
export function verifyRefs(
  token: string,
  secret: string,
): RefsPayload {
  const parts = token.split('.')
  if (parts.length !== 3) throw new Error('malformed token')

  const [header, body, signature] = parts
  const expected = hmac(`${header}.${body}`, secret)

  const expectedBuf = Buffer.from(expected)
  const signatureBuf = Buffer.from(signature!)
  if (expectedBuf.length !== signatureBuf.length || !timingSafeEqual(expectedBuf, signatureBuf)) {
    throw new Error('signature mismatch')
  }

  return JSON.parse(Buffer.from(body!, 'base64url').toString())
}

function base64url(obj: unknown): string {
  return Buffer.from(JSON.stringify(obj, (_key, value) =>
    typeof value === 'bigint' ? value.toString() : value
  )).toString('base64url')
}

function hmac(data: string, secret: string): string {
  return createHmac('sha256', secret).update(data).digest('base64url')
}
